import * as Yup from 'common/yup';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {BackupSettings, backupSettingsSchema} from 'types/BackupSettings';
import AsyncContainer from 'components/AsyncContainer';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Panel from 'components/Panel';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {settingsActions, settingsSelectors} from 'redux/settings';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import FormikNumberField from 'components/inputs/FormikNumberField';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import FormikTextField from 'components/inputs/FormikTextField';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {useSendData} from 'features/layout/CommonPage/redux/hooks/useSendData';
import TestDirectory from 'features/layout/CommonPage/panels/TransportPanel/components/TestDirectory';
import {isEmpty} from 'lodash';
import Formik from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import api from 'api';

const validationSchema = backupSettingsSchema.shape({
   repeatPassword: Yup.string().when('encryptBackup', {
      is: false,
      then: Yup.string(),
      otherwise: Yup.string()
         .oneOf([Yup.ref('password'), null], 'Passwords do not match')
         .required('Required'),
   }),
});

type FormProps = {
   ok: (values: BackupSettings, callbacks: any) => any;
   cancel: () => void;
};

const dailyBackupHourOptions = Array.from(Array(12).keys()).map((i) => String(i + 1));

const Form = ({cancel}: {cancel: FormProps['cancel']}) => {
   const {t} = useTranslation();
   const getValue = useFieldValueGetter<BackupSettings>();

   const backupDir = getValue('backupDir');
   const encryptBackup = getValue('encryptBackup');
   const passwordHistory = getValue('passwordHistory');
   const dailyBackups = getValue('dailyBackups');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   return (
      <>
         <FormikCheckbox autoFocus name="backupOnExit" label={t('Backup Reminder on Exit')} />
         <Panel name={t('Backup Reminder and Default Directory')}>
            <Grid container spacing={2} alignItems={'center'}>
               <Grid item xs={10}>
                  {/* TODO: 'Test' Dialog */}
                  <FormikBrowseFileInput
                     name="backupDir"
                     placeholder={t('Backup Directory')}
                     directoriesOnly
                  />
               </Grid>
               <Grid item xs={2}>
                  <TestDirectory directoryPath={backupDir} />
               </Grid>
            </Grid>
         </Panel>
         <FormikCheckbox name="encryptBackup" label={t('Encrypt Backup Files')} />
         <Panel name={t('Backup Encryption')}>
            <FormikPassphrase
               type="password"
               name="password"
               label={t('Backup File Password')}
               disabled={!encryptBackup}
               fetchUrl={api.getPasswordBackup}
            />
            <FormikTextField
               type="password"
               name="repeatPassword"
               label={t('Repeat Password')}
               disabled={!encryptBackup}
            />
         </Panel>
         <Panel name={t('Daily Backup')}>
            <Grid container spacing={2} alignItems={'center'}>
               <Grid item xs={12}>
                  <FormikCheckbox name="dailyBackups" label={t('Turn Off Daily Backup')} />
               </Grid>
               <Grid item xs={3}>
                  <Typography variant={'body2'}>{t('Time of day')}:</Typography>
               </Grid>
               <Grid item xs={4}>
                  <FormikDropdown
                     name="dailyBackupHour12"
                     options={dailyBackupHourOptions}
                     disabled={dailyBackups}
                  />
               </Grid>
               <Grid item xs={5}>
                  <FormikDropdown
                     name="dailyBackupHourAmOrPm"
                     options={['AM', 'PM']}
                     disabled={dailyBackups}
                  />
               </Grid>
               <Grid item xs={12}>
                  <FormikNumberField
                     name="dailyBackupRetention"
                     label={t('Retain Backup Files For')}
                     disabled={dailyBackups}
                     InputProps={{
                        endAdornment: <InputAdornment position={'end'}>{t('Days')}</InputAdornment>,
                     }}
                  />
               </Grid>
            </Grid>
         </Panel>
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <Box textAlign={'center'}>
            <FormFooterButtons cancel={cancel} />
         </Box>
      </>
   );
};

const BackupSettingsForm = ({ok, cancel}: FormProps) => {
   const settings = useSelector(settingsSelectors.backupSettings);
   const isFetching = useSelector(settingsSelectors.isFetching);
   const dispatch = useDispatch();
   React.useEffect(() => {
      dispatch(settingsActions.getBackupSettings());
   }, [dispatch]);

   const initialValues = {
      ...settings,
      repeatPassword: settings.password,
      dailyBackups: !settings.dailyBackups,
      dailyBackupHour12: String(
         settings.dailyBackupHour % 12 === 0 ? 12 : settings.dailyBackupHour % 12
      ),
      dailyBackupHourAmOrPm: Math.floor(settings.dailyBackupHour / 12) === 0 ? 'AM' : 'PM',
   };

   return (
      <AsyncContainer loading={isFetching}>
         {isEmpty(settings) ? null : (
            <Formik
               initialValues={initialValues}
               onSubmit={(values, helpers) => {
                  values.dailyBackups = !values.dailyBackups;
                  values.dailyBackupHour =
                     (+values.dailyBackupHour12 === 12 ? 0 : +values.dailyBackupHour12) +
                     (values.dailyBackupHourAmOrPm === 'PM' ? 12 : 0);
                  const {
                     repeatPassword,
                     dailyBackupHour12,
                     dailyBackupHourAmOrPm,
                     ...restValues
                  } = values;

                  return ok(restValues, helpers);
               }}
               validationSchema={validationSchema}
               enableReinitialize
            >
               <Form cancel={cancel} />
            </Formik>
         )}
      </AsyncContainer>
   );
};

type DialogProps = {
   onClose: () => void;
};
export const BackupSettingsDialog = ({onClose}: DialogProps) => {
   const sendData = useSendData();
   return (
      <Dialog maxWidth="sm" header={DialogTypes.BackupSettingsDialog.dialogName} close={onClose}>
         <BackupSettingsForm
            ok={(values, callbacks) => {
               sendData(DialogTypes.BackupSettingsDialog.id, values, callbacks);
               onClose();
            }}
            cancel={onClose}
         />
      </Dialog>
   );
};

export default React.memo(BackupSettingsDialog);
